/* ─────────────────────────────────────────────────────────────
   Shared marketing content (used by Home, Services, Technology…)
   Positioning: full-service, technology-agnostic, startups → enterprise.
───────────────────────────────────────────────────────────── */

export const CONTACT = {
  phone: '+91 75587 93773',
  phoneHref: 'tel:+917558793773',
  email: 'contact@quantum-stack.com',
  linkedin: 'https://www.linkedin.com/company/quantum-stack/',
  whatsapp:
    'https://wa.me/917558793773?text=Hi%20Quantum%20Stack%2C%20I%27d%20like%20to%20discuss%20a%20project.',
};

/* Verified office location (Google Maps listing "Quantum Stack Pvt Ltd") */
const LAT = 18.6508414;
const LNG = 73.7067072;

export const LOCATION = {
  lines: ['1304, B, Terra Greens,', 'Sangavade, Pimpri-Chinchwad,', 'Maharashtra 411033, India'],
  short: 'Pimpri-Chinchwad, Pune, India',
  plusCode: 'MP24+8M Pimpri-Chinchwad, Maharashtra',
  lat: LAT,
  lng: LNG,
  /** Official Google Maps listing */
  mapsUrl: 'https://maps.app.goo.gl/t5Fc2ad7Y9T1cd2RA',
  /** Turn-by-turn directions to the exact pin */
  directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${LAT},${LNG}`,
  /** Embeddable map centred on the exact pin */
  embedUrl: `https://maps.google.com/maps?q=${LAT},${LNG}&z=16&output=embed`,
};

export const FOUNDED_YEAR = 2024;

export const ALL_SERVICES = [
  {
    icon: 'web',
    title: 'Web Application Development',
    body: 'Fast, secure, SEO-ready websites, portals and web apps — built on React, Next.js, Angular, Vue or the stack that fits your product.',
    to: '/solutions/custom-software',
  },
  {
    icon: 'mobile',
    title: 'Mobile App Development',
    body: 'iOS and Android apps with React Native, Flutter or native Swift/Kotlin — polished UX, offline support and store-ready delivery.',
    to: '/contact',
  },
  {
    icon: 'rocket',
    title: 'SaaS & MVP Development',
    body: 'Go from idea to launch quickly. Multi-tenant architecture, subscription billing and analytics — ready to scale when you grow.',
    to: '/solutions/saas-development',
  },
  {
    icon: 'building',
    title: 'Enterprise & ERP Software',
    body: 'Custom ERP, CRM, workflow and operations platforms engineered around how your business actually works.',
    to: '/solutions/enterprise-software',
  },
  {
    icon: 'ai',
    title: 'AI, ML & Generative AI',
    body: 'LLM-powered assistants, RAG search, document automation, predictive analytics and computer vision — shipped to production.',
    to: '/solutions/ai-ml-solutions',
  },
  {
    icon: 'cloud',
    title: 'Cloud & DevOps',
    body: 'AWS, Azure and Google Cloud. CI/CD, containers, Kubernetes, infrastructure-as-code, monitoring and cost optimisation.',
    to: '/technology',
  },
  {
    icon: 'api',
    title: 'API & System Integration',
    body: 'REST, GraphQL and event-driven integrations — payments, CRMs, ERPs, WhatsApp, third-party and legacy systems.',
    to: '/technology',
  },
  {
    icon: 'design',
    title: 'UI/UX & Product Design',
    body: 'Research, wireframes, clickable prototypes and design systems that make products easy to love and easy to use.',
    to: '/contact',
  },
  {
    icon: 'test',
    title: 'QA & Test Automation',
    body: 'Manual and automated testing, performance and security testing — so what you launch works the first time.',
    to: '/our-process',
  },
  {
    icon: 'data',
    title: 'Data Engineering & Analytics',
    body: 'Pipelines, warehouses and live dashboards that turn raw data into decisions your team can act on.',
    to: '/solutions/ai-ml-solutions',
  },
  {
    icon: 'refresh',
    title: 'Legacy Modernisation',
    body: 'Re-platform, refactor or migrate ageing systems to modern, maintainable architecture — without stopping the business.',
    to: '/industries/enterprise',
  },
  {
    icon: 'users',
    title: 'Support & Dedicated Teams',
    body: 'Ongoing maintenance, SLAs, or a dedicated engineering team that works as an extension of yours.',
    to: '/contact',
  },
];

export const STACK_GROUPS = [
  { name: 'Backend', items: ['Java · Spring Boot', 'Node.js · NestJS', 'Python · Django · FastAPI', '.NET · C#', 'Go', 'PHP · Laravel'] },
  { name: 'Frontend', items: ['React', 'Next.js', 'Angular', 'Vue.js', 'TypeScript', 'Tailwind CSS'] },
  { name: 'Mobile', items: ['React Native', 'Flutter', 'Swift · iOS', 'Kotlin · Android'] },
  { name: 'Data', items: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'Elasticsearch', 'Kafka'] },
  { name: 'Cloud & DevOps', items: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Terraform', 'CI / CD'] },
  { name: 'AI & Automation', items: ['OpenAI · LLMs', 'LangChain · RAG', 'TensorFlow', 'PyTorch', 'Vector databases', 'Hugging Face'] },
];

export const MARQUEE_TECH = [
  'Java', 'Spring Boot', 'Node.js', 'Python', '.NET', 'Go', 'React', 'Next.js', 'Angular', 'Vue.js',
  'React Native', 'Flutter', 'PostgreSQL', 'MongoDB', 'Redis', 'Kafka', 'AWS', 'Azure', 'Google Cloud',
  'Docker', 'Kubernetes', 'Terraform', 'OpenAI', 'TensorFlow',
];

export const ENGAGEMENT_MODELS = [
  {
    icon: 'target',
    title: 'Fixed-Price Project',
    body: 'Clear scope, clear price, clear timeline. Ideal for MVPs, websites and well-defined products.',
    points: ['Detailed scope & milestones', 'Predictable budget', 'Demo at every milestone'],
  },
  {
    icon: 'users',
    title: 'Dedicated Team',
    body: 'A full engineering squad — developers, QA, design and a delivery lead — working only on your product.',
    points: ['Scale up or down monthly', 'Direct daily communication', 'Long-term product ownership'],
  },
  {
    icon: 'bolt',
    title: 'Staff Augmentation',
    body: 'Add senior engineers to your existing team within days, for as long as you need them.',
    points: ['Pre-vetted engineers', 'Flexible duration', 'Works in your tools & process'],
  },
  {
    icon: 'compass',
    title: 'Discovery Sprint',
    body: 'Not sure where to start? A short paid sprint to define scope, architecture and a realistic roadmap.',
    points: ['Architecture & estimate', 'Clickable prototype option', 'Zero lock-in'],
  },
];

export const HOME_FAQS = [
  {
    question: 'Which technologies and platforms do you work with?',
    answer:
      'We are technology-agnostic and pick the stack that best fits your product, budget and team. Our engineers work across Java, Node.js, Python, .NET and Go on the backend; React, Next.js, Angular and Vue on the web; React Native, Flutter, Swift and Kotlin for mobile; and AWS, Azure and Google Cloud for infrastructure. If you already have a preferred stack, we will work in it.',
  },
  {
    question: 'We are a startup with a small budget. Can you still help?',
    answer:
      'Yes. We work with founders, startups, SMEs and large enterprises. For early-stage products we recommend a focused MVP, fixed-price scope and an architecture that can grow with you — so you launch fast without paying for a rebuild later.',
  },
  {
    question: 'How much does a project cost and how is it priced?',
    answer:
      'It depends on scope. We offer fixed-price projects, dedicated teams and time-and-material engagements. Share your idea and we will give you a free consultation and a clear, itemised estimate — no obligation.',
  },
  {
    question: 'How quickly can you start?',
    answer:
      'We keep onboarding fast. After a short discovery call and scope alignment, most projects kick off soon after sign-off, and staff-augmentation engineers can join your team within days.',
  },
  {
    question: 'Who owns the source code and intellectual property?',
    answer:
      'You do. All source code, designs, documentation and IP are transferred to you in full. We are happy to sign an NDA before any discussion.',
  },
  {
    question: 'Do you offer support after launch?',
    answer:
      'Yes. We provide maintenance, monitoring, feature development and SLA-based support — or a dedicated team that continues to evolve the product with you.',
  },
  {
    question: 'Can you work with our existing team or codebase?',
    answer:
      'Absolutely. We can audit, stabilise, extend and modernise existing systems, and embed with your in-house team using your tools and processes.',
  },
  {
    question: 'We are outside India. How do you handle time zones and communication?',
    answer:
      'We are remote-first and work with teams in any time zone. We agree overlap hours up front, run regular demos and give you full visibility through shared boards and repositories.',
  },
];

/* Our own products (built and owned by Quantum Stack) */
export const PRODUCTS = [
  {
    slug: 'restaurant-erp',
    icon: 'utensils',
    name: 'Restaurant ERP Platform',
    domain: 'ERP & Operations',
    tagline: 'One system for the whole restaurant back office.',
    description:
      'A multi-module back-office platform for restaurant and restaurant-chain management covering procurement, inventory, recipe costing, vendor payments and operational reporting — replacing disconnected spreadsheets with a single, role-based interface.',
    highlights: ['Procurement & inventory control', 'Recipe costing', 'Vendor payments', 'Role-based access & reporting'],
    stack: 'Spring Boot · React.js · MySQL · REST API',
  },
  {
    slug: 'online-examination',
    icon: 'exam',
    name: 'Online Examination System',
    domain: 'EdTech Platform',
    tagline: 'Run assessments at scale, securely.',
    description:
      'A structured examination platform for institutions conducting assessments at scale — question-bank management, timed test delivery, anti-cheating controls, automated result computation and administrative reporting.',
    highlights: ['Question bank management', 'Timed test delivery', 'Anti-cheating controls', 'Automated results & reports'],
    stack: 'Spring Boot · React.js · MySQL · JWT Authentication',
  },
  {
    slug: 'vehicle-qr',
    icon: 'qr',
    name: 'Vehicle QR Code Management System',
    domain: 'Operations & Compliance',
    tagline: 'Scan, update and track every vehicle.',
    description:
      'A QR-code-based lifecycle tracking system for vehicle fleets. Field staff scan and update vehicle records, service history and compliance documents from mobile devices, with a central admin portal for fleet managers.',
    highlights: ['QR scan from any mobile device', 'Service history tracking', 'Compliance document records', 'Central admin portal'],
    stack: 'Spring Boot · React.js · MySQL · QR Code Integration',
  },
];
